import { AuthError } from "aws-amplify/auth"
import type { Namespace, TFunction } from "i18next"
import { UseFormReturn } from "react-hook-form"

import { TranslationKey } from "./types/local"

// Define known AWS Cognito error types
type CognitoErrorName =
  | "UsernameExistsException"
  | "UserNotFoundException"
  | "InvalidParameterException"
  | "InvalidPasswordException"
  | "NotAuthorizedException"
  | "CodeMismatchException"
  | "ExpiredCodeException"
  | "LimitExceededException"
  | "TooManyRequestsException"
  | "UserNotConfirmedException"
  | "UserLambdaValidationException" // PreSignUp trigger rejection

/*
  Unlike our own API, Cognito gives us no structured failReason for a PreSignUp
  trigger rejection: the Lambda contract carries only a string, which Cognito
  surfaces as a UserLambdaValidationException whose message embeds the code, e.g.
  "PreSignUp failed with error PhoneNumberInUse: An account with this phone
  number already exists." These codes are the backend's PreSignUp contract
*/
const PRE_SIGN_UP_PHONE_ERRORS: Record<string, TranslationKey<"errors">> = {
  PhoneNumberInUse: "auth.cognitoErrors.phoneNumberInUse",
  PhoneNumberRequired: "auth.cognitoErrors.phoneNumberRequired",
  InvalidPhoneNumber: "auth.cognitoErrors.invalidPhoneNumber",
}

// Scans a wrapped Cognito message for the first known code above and returns
// its translation key, or undefined if the message carries none — which is the
// case for the fail-closed uniqueness error, so callers must handle the miss.
// First match wins, so no code may be a substring of another.
function preSignUpPhoneError(message: string) {
  return Object.entries(PRE_SIGN_UP_PHONE_ERRORS).find(([code]) =>
    message.includes(code)
  )?.[1]
}

// Authentication error interface
interface AuthenticationError {
  name: CognitoErrorName | string
  message: string
}

function setGenericError(
  form: UseFormReturn<any>,
  t: TFunction<Namespace>,
  message: string
): void {
  form.setError("root", {
    message: t("auth.cognitoErrors.defaultError", {
      ns: "errors",
      message: message || "Unknown error",
    }),
  })
}

function isAuthenticationError(error: unknown): error is AuthenticationError {
  return (
    error instanceof AuthError ||
    (typeof error === "object" &&
      error !== null &&
      "name" in error &&
      "message" in error &&
      typeof (error as any).name === "string" &&
      typeof (error as any).message === "string")
  )
}

export function handleAuthError(
  err: unknown,
  form: UseFormReturn<any>,
  t: TFunction<Namespace>
): void {
  if (!isAuthenticationError(err)) {
    form.setError("root", {
      message: t("auth.cognitoErrors.defaultError", {
        ns: "errors",
      }),
    })
    return
  }

  switch (err.name) {
    // Email-specific errors
    case "UsernameExistsException":
      form.setError("email", {
        message: t("auth.cognitoErrors.usernameExists", {
          ns: "errors",
        }),
      })
      break
    case "UserNotFoundException":
      form.setError("root", {
        message: t("auth.cognitoErrors.userNotFound", { ns: "errors" }),
      })
      break
    case "InvalidParameterException": {
      // Check the error message to determine which field is invalid
      const message = err.message.toLowerCase()
      if (message.includes("phone")) {
        form.setError("phone", {
          message: t("common.validPhone", { ns: "errors" }),
        })
      } else {
        // Default to email for other invalid parameter errors
        form.setError("email", {
          message: t("auth.cognitoErrors.invalidParameter", {
            ns: "errors",
          }),
        })
      }
      break
    }

    // Sign-up trigger errors
    case "UserLambdaValidationException": {
      const translationKey = preSignUpPhoneError(err.message)
      if (translationKey) {
        form.setError("phone", {
          message: t(translationKey, { ns: "errors" }),
        })
      } else {
        // No recognized token (e.g. the fail-closed uniqueness check)
        setGenericError(form, t, err.message)
      }
      break
    }

    // Password-specific errors
    case "InvalidPasswordException": {
      // Check if form has a newPassword field, otherwise use password field
      const passwordField = Object.prototype.hasOwnProperty.call(
        form.getValues(),
        "newPassword"
      )
        ? "newPassword"
        : "password"
      form.setError(passwordField, {
        message: t("auth.cognitoErrors.invalidPassword", {
          ns: "errors",
        }),
      })
      break
    }
    case "NotAuthorizedException":
      // Could be incorrect password or general auth failure
      form.setError("root", {
        message: t("auth.cognitoErrors.notAuthorized", { ns: "errors" }),
      })
      break

    // Verification code errors
    case "CodeMismatchException":
      form.setError("root", {
        message: t("auth.cognitoErrors.codeMismatch", { ns: "errors" }),
      })
      break
    case "ExpiredCodeException":
      form.setError("root", {
        message: t("auth.cognitoErrors.expiredCode", { ns: "errors" }),
      })
      break

    // Rate limiting errors
    case "LimitExceededException":
      form.setError("root", {
        message: t("auth.cognitoErrors.limitExceeded", { ns: "errors" }),
      })
      break
    case "TooManyRequestsException":
      form.setError("root", {
        message: t("auth.cognitoErrors.tooManyRequests", {
          ns: "errors",
        }),
      })
      break

    // User confirmation errors
    case "UserNotConfirmedException":
      form.setError("root", {
        message: t("auth.cognitoErrors.userNotConfirmed", {
          ns: "errors",
        }),
      })
      break

    // Default fallback
    default:
      setGenericError(form, t, err.message)
  }
}
